import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Divider,
} from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  IPartNumberDtoResponse,
  IPartNumberDtoUpdate,
  IWarehouseLocation,
} from "../../../models/Warehouse";
import { RootState } from "../../../store";
import { useUpdateWarehouse } from "../../../query/WarehouseQuery";
import { notify } from "../../../utils/toast";

interface ModalEditWarehouseProps {
  open: boolean;
  record?: IPartNumberDtoResponse | null;
  onCancel: () => void;
}

const ModalEditWarehouse = ({
  open,
  record,
  onCancel,
}: ModalEditWarehouseProps) => {
  const [form] = Form.useForm();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<
    number | undefined
  >();
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  );
  const updateMutation = useUpdateWarehouse(record?.id!);

  const selectedWarehouse = locations.find((w) => w.id === selectedWarehouseId);

  useEffect(() => {
    if (record) {
      let locationId: number | undefined;
      let warehouseId: number | undefined;

      locations.forEach((warehouse: IWarehouseLocation) => {
        const foundLocation = warehouse.warehouseLocations.find(
          (loc) => loc.code === record.location?.code,
        );
        if (foundLocation) {
          locationId = foundLocation.id;
          warehouseId = warehouse.id;
        }
      });

      form.setFieldsValue({
        project: record.project,
        projectCode: record.projectCode,
        partNumber: record.partNumber,
        manufacturer: record.manufacturer,
        unit: record.unit,
        invoiceQuantity: record.invoiceQuantity,
        actualQuantity: record.actualQuantity,
        awb: record.awb,
        internalCode: record.internalCode,
        description: record.description,
        note: record.note,
        noteExt: record.noteExt,
        supplierType: record.supplierType,
        purchaser: record.purchaser,
        warehouseCode: warehouseId,
        locationId: locationId,
      });
      setSelectedWarehouseId(warehouseId);
    } else {
      form.resetFields();
      setSelectedWarehouseId(undefined);
    }
  }, [record, form, locations]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!record) return;
      const { warehouseCode, ...updateData } = values;
      await updateMutation.mutateAsync({
        ...updateData,
      });
      notify.success("Cập nhật thành công");
      onCancel();
    } catch (e) {
      notify.error("Cập nhật thất bại");
    }
  };

  const handleWarehouseChange = (warehouseId: number) => {
    setSelectedWarehouseId(warehouseId);
    form.setFieldValue("locationId", undefined);
  };

  return (
    <Modal
      open={open}
      title="Sửa thông tin mã hàng"
      onOk={handleOk}
      confirmLoading={updateMutation.status === "pending"}
      onCancel={() => {
        form.resetFields();
        setSelectedWarehouseId(undefined);
        onCancel();
      }}
      okText="Lưu"
      cancelText="Hủy"
      width="95%"
      style={{ top: 20, maxWidth: "1400px" }}
      styles={{
        body: {
          height: "calc(100vh - 200px)",
          overflowY: "auto",
          padding: "24px",
        },
      }}
    >
      <Form form={form} layout="vertical">
        <Divider orientation="left" style={{ marginTop: 0, marginBottom: 24 }}>
          Thông tin sản phẩm
        </Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Mã dự án"
              name="projectCode"
              rules={[{ required: true, message: "Vui lòng nhập mã dự án" }]}
            >
              <Input placeholder="Nhập mã dự án" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Tên dự án" name="project">
              <Input placeholder="Nhập tên dự án" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Part Number"
              name="partNumber"
              rules={[{ required: true, message: "Vui lòng nhập mã hàng" }]}
            >
              <Input placeholder="Nhập Part Number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Nhà cung cấp" name="manufacturer">
              <Input placeholder="Nhập nhà cung cấp" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Mã nội bộ" name="internalCode">
              <Input placeholder="Nhập mã nội bộ" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Đơn vị tính" name="unit">
              <Input placeholder="Nhập đơn vị tính" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Mô tả" name="description">
              <Input.TextArea placeholder="Nhập mô tả sản phẩm" rows={2} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 24, marginBottom: 24 }}>
          Số lượng & Kho
        </Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Invoice Quantity"
              name="invoiceQuantity"
              rules={[{ required: true, message: "Vui lòng nhập số lượng" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Actual Quantity"
              name="actualQuantity"
              rules={[{ required: true, message: "Vui lòng nhập số lượng" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="AWB" name="awb">
              <Input placeholder="Nhập AWB" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Mã kho" name="warehouseCode">
              <Select
                showSearch
                placeholder="Chọn mã kho"
                onChange={handleWarehouseChange}
                filterOption={(input, option) => {
                  const label = option?.label?.toString() ?? "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {locations.map((loc: IWarehouseLocation) => (
                  <Select.Option key={loc.id} value={loc.id}>
                    {loc.warehouseCode}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24}>
            <Form.Item label="Vị trí kho" name="locationId">
              <Select
                showSearch
                placeholder={
                  selectedWarehouse
                    ? "Chọn vị trí kho"
                    : "Vui lòng chọn mã kho trước"
                }
                disabled={!selectedWarehouse}
                filterOption={(input, option) => {
                  const label = option?.label?.toString() ?? "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {selectedWarehouse ? (
                  selectedWarehouse.warehouseLocations.map((subloc) => (
                    <Select.Option key={subloc.id} value={subloc.id}>
                      {subloc.code}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">
                    Không có vị trí nào
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 24, marginBottom: 24 }}>
          Thông tin khác
        </Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Loại nhà cung cấp" name="supplierType">
              <Input placeholder="Nhập loại nhà cung cấp" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Người mua" name="purchaser">
              <Input placeholder="Nhập người mua" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item label="Ghi chú" name="note">
              <Input.TextArea placeholder="Nhập ghi chú" rows={2} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item label="Ghi chú 2" name="noteExt">
              <Input.TextArea placeholder="Nhập ghi chú bổ sung" rows={2} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalEditWarehouse;
